import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectProjectIdentity } from '@store/actionSlices/projectIdentity'
import { ExtendedUnitFilterOptions } from '@store/types'

import getFilteredBuildingData, {
  FilterBuildingData,
} from '@utilities/filter-building'

import { TAP_MAX_POINTER_TRAVEL } from './constants'
import { sortLevelsHighestFirst } from './level-explorer-util'
import LevelList from './level-list'
import LevelUnitsPanel from './level-units-panel'

interface LevelExplorerProps {
  building: FilterBuildingData
  blockCount: number
  unitFilter: ExtendedUnitFilterOptions
  selectedLevelId: string
  onSelectLevel: (levelId: string) => void
  onSeeFloorplateView: (levelId: string) => void
  onSelectUnit: (levelId: string, unitName: string) => void
}

function LevelExplorer({
  building,
  blockCount,
  unitFilter,
  selectedLevelId,
  onSelectLevel,
  onSeeFloorplateView,
  onSelectUnit,
}: LevelExplorerProps) {
  const { showPrice, statusLabels } = useSelector(selectProjectIdentity)
  const isFilterApplied = unitFilter.apply

  const { listLevels, matchingLevelsById } = useMemo(() => {
    const getLevels = (filter: ExtendedUnitFilterOptions) =>
      getFilteredBuildingData(
        building,
        filter,
        showPrice,
        blockCount,
        statusLabels?.available
      )

    const blockLevels = getLevels({ ...unitFilter, apply: false })
    const matchingLevels = isFilterApplied
      ? getLevels({ ...unitFilter, anyLevel: true })
      : blockLevels
    const matchingById = new Map(
      matchingLevels.map((level) => [level.level, level])
    )

    return {
      listLevels: sortLevelsHighestFirst(
        blockLevels.map(
          (level) => matchingById.get(level.level) ?? { ...level, data: [] }
        )
      ),
      matchingLevelsById: matchingById,
    }
  }, [
    building,
    blockCount,
    unitFilter,
    isFilterApplied,
    showPrice,
    statusLabels,
  ])

  const selectedLevel = matchingLevelsById.get(selectedLevelId)

  useEffect(() => {
    if (selectedLevelId && !selectedLevel) {
      onSelectLevel('')
    }
  }, [selectedLevelId, selectedLevel, onSelectLevel])

  useEffect(() => {
    let pointerDownPosition = { x: 0, y: 0 }

    const handlePointerDown = (event: PointerEvent) => {
      pointerDownPosition = { x: event.clientX, y: event.clientY }
    }

    const handleCanvasTap = (event: MouseEvent) => {
      if (!(event.target instanceof HTMLCanvasElement)) {
        return
      }
      const pointerTravel = Math.hypot(
        event.clientX - pointerDownPosition.x,
        event.clientY - pointerDownPosition.y
      )
      if (pointerTravel > TAP_MAX_POINTER_TRAVEL) {
        return
      }
      onSelectLevel('')
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('click', handleCanvasTap)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('click', handleCanvasTap)
    }
  }, [onSelectLevel])

  return (
    <>
      <LevelList
        key={building.activeBlock}
        levels={listLevels}
        selectedLevelId={selectedLevelId}
        onSelectLevel={onSelectLevel}
      />
      {selectedLevel && (
        <LevelUnitsPanel
          level={selectedLevel}
          isFilterApplied={isFilterApplied}
          onClose={() => onSelectLevel('')}
          onSeeFloorplateView={() => onSeeFloorplateView(selectedLevel.level)}
          onSelectUnit={(unit) => onSelectUnit(selectedLevel.level, unit.name)}
        />
      )}
    </>
  )
}

export default LevelExplorer
